import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface QuizOption {
  id: string;
  text: string;
}

interface QuizQuestion {
  id: number;
  question: string;
  options: QuizOption[];
  answer: string;
}

interface QuizData {
  quizTitle: string;
  description: string;
  questions: QuizQuestion[];
  settings: {
    shuffleQuestions: boolean;
    shuffleOptions: boolean;
    timeLimit: number;
  };
}

interface QuizProps {
  quizData: QuizData;
  onComplete: () => void;
  isDark: boolean;
}

export const Quiz: React.FC<QuizProps> = ({ quizData, onComplete, isDark }) => {
  const { t } = useTranslation();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [showResults, setShowResults] = useState(false);

  const handleAnswer = (questionId: number, answerId: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: answerId }));
  };

  const calculateScore = () => {
    let correct = 0;
    quizData.questions.forEach(question => {
      if (answers[question.id] === question.answer) {
        correct++;
      }
    });
    return (correct / quizData.questions.length) * 100;
  };

  const handleSubmit = () => {
    const score = calculateScore();
    setShowResults(true);
    if (score >= 70) { // Pass threshold
      onComplete();
    }
  };

  const currentQ = quizData.questions[currentQuestion];

  return (
    <div className={`card ${isDark ? 'bg-base-200' : 'bg-base-100'} shadow-xl`}>
      <div className="card-body">
        <h2 className="card-title">{quizData.quizTitle}</h2>
        <p className="text-sm opacity-75">{quizData.description}</p>

        {!showResults ? (
          <>
            <div className="my-4">
              <h3 className="font-semibold mb-4">{currentQ.question}</h3>
              <div className="space-y-2">
                {currentQ.options.map((option) => (
                  <label key={option.id} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name={`question-${currentQ.id}`}
                      value={option.id}
                      checked={answers[currentQ.id] === option.id}
                      onChange={() => handleAnswer(currentQ.id, option.id)}
                      className="radio radio-primary"
                    />
                    <span>{option.text}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="card-actions justify-between">
              <button
                className="btn btn-ghost"
                disabled={currentQuestion === 0}
                onClick={() => setCurrentQuestion(prev => prev - 1)}
              >
                {t('quiz.previous')}
              </button>
              {currentQuestion < quizData.questions.length - 1 ? (
                <button
                  className="btn btn-primary"
                  disabled={!answers[currentQ.id]}
                  onClick={() => setCurrentQuestion(prev => prev + 1)}
                >
                  {t('quiz.next')}
                </button>
              ) : (
                <button
                  className="btn btn-primary"
                  disabled={!answers[currentQ.id]}
                  onClick={handleSubmit}
                >
                  {t('quiz.submit')}
                </button>
              )}
            </div>
          </>
        ) : (
          <div className="text-center">
            <h3 className="text-xl font-bold mb-4">
              {t('quiz.score')}: {calculateScore()}%
            </h3>
            {calculateScore() >= 70 ? (
              <div className="alert alert-success">
                {t('quiz.pass_message')}
              </div>
            ) : (
              <div className="alert alert-error">
                {t('quiz.fail_message')}
                <button
                  className="btn btn-primary mt-4"
                  onClick={() => {
                    setShowResults(false);
                    setAnswers({});
                    setCurrentQuestion(0);
                  }}
                >
                  {t('quiz.try_again')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};